import { find } from "lodash";
import React from "react";
import PropTypes from "prop-types";

import Button from "antd/lib/button";
import DynamicComponent from "@/components/DynamicComponent";
import { DataSource as DataSourceType, UserProfile as UserType } from "@/components/proptypes";

import { currentUser } from "@/services/auth";
import notification from "@/services/notification";
import { useTheme } from "@/components/ThemeProvider";
import useIndexerDataSource from './useIndexDataSource';

import "./IndexerDestinations.less";

function ListItem({ dataSource, user, onRemove }) {
  const canRemove = currentUser.id === user.id;
  const { isDarkMode } = useTheme();
  
  return (
    <li className={`destination-wrapper ${isDarkMode ? 'dark-mode' : ''}`}>
      <img 
        src={dataSource.icon || `${window.location.origin}/static/images/db-logos/unknown.png`} 
        className="destination-icon"
        alt={dataSource.name}
      />
      <span className="flex-fill">{dataSource.name}</span>
      {canRemove && (
        <Button 
          type="link" 
          size="small" 
          danger
          onClick={onRemove}
        >
          Remove
        </Button>
      )}
    </li>
  );
}

ListItem.propTypes = {
  dataSource: DataSourceType.isRequired,
  user: UserType.isRequired,
  onRemove: PropTypes.func.isRequired,
};

function IndexerDestinations({ indexerId, onDataSourceSelect, viewMode, dataSourceId }) {
  const [localIndexer, setLocalIndexer] = React.useState({ 
    id: indexerId,
    data_source_id: dataSourceId 
  });
  const { isDarkMode } = useTheme();
  
  const isNewIndexer = !indexerId || indexerId === "new";
  
  // Use the hook to load data sources
  const { dataSourcesLoaded, dataSources, dataSource } = useIndexerDataSource(localIndexer);
  
  // Update localIndexer when data source is loaded or dataSourceId changes
  React.useEffect(() => {
    if (dataSource) {
      setLocalIndexer(prev => ({
        ...prev,
        data_source_id: dataSource.id
      }));
    } else if (dataSourceId) {
      setLocalIndexer(prev => ({
        ...prev,
        data_source_id: dataSourceId
      }));
    }
  }, [dataSource, dataSourceId]);
  
  const handleDataSourceChange = (dataSourceId) => {
    // Only process changes if not in view mode
    if (viewMode) return;
    
    // Handle the case when data source is cleared (null)
    if (!dataSourceId) {
      if (isNewIndexer) {
        // For new indexers, just update local state and notify parent
        setLocalIndexer(prev => ({ ...prev, data_source_id: null }));
        if (onDataSourceSelect) {
          onDataSourceSelect(null);
        }
        return;
      } else {
        // For existing indexers, remove the data source
        return removeDataSource();
      }
    }
    
    const selectedDataSource = find(dataSources, { id: dataSourceId });
    if (!selectedDataSource) return;
    
    if (isNewIndexer) {
      // For new indexers, just update local state and notify parent
      setLocalIndexer(prev => ({ ...prev, data_source_id: dataSourceId }));
      if (onDataSourceSelect) {
        onDataSourceSelect(selectedDataSource);
      }
    } else {
      // For existing indexers, make the API call
      updateDataSource(selectedDataSource)
        .then(() => {
          notification.success("Data source updated.");
        })
        .catch(() => {
          notification.error("Failed updating data source.");
        });
    }
  };

  const updateDataSource = (dataSource) => {
    // Only make API call for existing indexers
    if (isNewIndexer) {
      return Promise.resolve();
    }
    
    return fetch(`/api/indexers/${indexerId}/data_source`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ data_source_id: dataSource.id }),
    }).then(response => {
      if (response.ok) {
        setLocalIndexer(prev => ({ ...prev, data_source_id: dataSource.id }));
        return response.json();
      }
      throw new Error("Failed to update data source");
    });
  };

  const removeDataSource = () => {
    if (isNewIndexer) {
      // For new indexers, just update local state and notify parent
      setLocalIndexer(prev => ({ ...prev, data_source_id: null }));
      if (onDataSourceSelect) {
        onDataSourceSelect(null);
      }
      notification.success("Data source removed.");
      return Promise.resolve();
    }
    
    // For existing indexers, make the API call
    return fetch(`/api/indexers/${indexerId}/data_source`, {
      method: "DELETE",
    }).then(response => {
      if (response.ok) {
        setLocalIndexer(prev => ({ ...prev, data_source_id: null }));
        notification.success("Data source removed.");
      } else {
        notification.error("Failed removing data source.");
        throw new Error("Failed to remove data source");
      }
    });
  };

  return (
    <div className={`indexer-destinations ${isDarkMode ? 'dark-mode' : ''}`}>
      <div className="data-source-selector">
        <DynamicComponent
          name={"QuerySourceDropdown"}
          dataSources={dataSources}
          value={localIndexer.data_source_id || (dataSource ? dataSource.id : undefined)}
          disabled={!dataSourcesLoaded || viewMode}
          loading={!dataSourcesLoaded}
          onChange={handleDataSourceChange}
        />
      </div>

      {!dataSourcesLoaded && (
        <div className="loading-message">
          <p>Loading data sources...</p>
        </div>
      )}
    </div>
  );
}

IndexerDestinations.propTypes = {
  indexerId: PropTypes.any,
  onDataSourceSelect: PropTypes.func,
  viewMode: PropTypes.bool,
  dataSourceId: PropTypes.number,
};

IndexerDestinations.defaultProps = {
  indexerId: null,
  onDataSourceSelect: null,
  viewMode: false,
  dataSourceId: null,
};

export default IndexerDestinations;
